import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Trophy, 
  Users, 
  Play, 
  CheckCircle, 
  XCircle, 
  Plus, 
  RefreshCw, 
  Clock, 
  Zap, 
  Target, 
  RotateCcw, 
  UserPlus, 
  Edit3, 
  ArrowRight, 
  ArrowLeft,
  ChevronRight, 
  ChevronDown, 
  Trash2 
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';

interface DragRacingTournamentManagerProps {
  eventId: string;
  categoryId: string;
  categoryName: string;
  onBracketCreated?: (bracketId: string) => void;
}

interface EventRegistration {
  id: string;
  user_id: string | null;
  temporary_user_id: string | null;
  user?: {
    full_name: string;
    email: string;
    phone: string;
  };
  temporary_user?: {
    name: string;
    race_number: string;
  };
  driver_profile?: {
    vehicle_info: any;
  };
}

interface TournamentBracket {
  id: string;
  event_id: string;
  category_id: string;
  name: string;
  bracket_type: 'single_elimination' | 'double_elimination' | 'round_robin' | 'advanced_elimination' | 'qualifying' | 'elimination';
  total_rounds: number;
  current_round: number;
  status: string;
  bracket_style?: string;
  qualifying_completed?: boolean;
  created_at: string;
  updated_at: string;
}

interface TournamentMatch {
  id: string;
  bracket_id: string;
  round_number: number;
  match_number: number;
  driver1_registration_id?: string;
  driver2_registration_id?: string;
  winner_registration_id?: string;
  loser_registration_id?: string;
  status: string;
  driver1_dial_in?: number;
  driver2_dial_in?: number;
  driver1_elapsed_time?: number;
  driver2_elapsed_time?: number;
  driver1_reaction_time?: number;
  driver2_reaction_time?: number;
  driver1_foul?: boolean;
  driver2_foul?: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface MatchResult {
  matchId: string;
  winnerRegistrationId: string;
  loserRegistrationId: string;
  winnerDialIn?: number;
  loserDialIn?: number;
  winnerElapsedTime?: number;
  loserElapsedTime?: number;
  winnerReactionTime?: number;
  loserReactionTime?: number;
  winnerFoul?: boolean;
  loserFoul?: boolean;
  notes?: string;
}

interface DriverAssignment {
  registrationId: string;
  driverName: string;
  raceNumber?: string;
}

interface BracketStatistics {
  total_matches: number;
  completed_matches: number;
  pending_matches: number;
  active_matches: number;
  assigned_matches: number;
  unassigned_matches: number;
}

export default function DragRacingTournamentManager({
  eventId,
  categoryId,
  categoryName,
  onBracketCreated
}: DragRacingTournamentManagerProps) {
  const [bracket, setBracket] = useState<TournamentBracket | null>(null);
  const [matches, setMatches] = useState<TournamentMatch[]>([]);
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<TournamentMatch | null>(null);
  const [showMatchResultModal, setShowMatchResultModal] = useState(false);
  const [showDriverAssignmentModal, setShowDriverAssignmentModal] = useState(false);
  const [matchResult, setMatchResult] = useState<Partial<MatchResult>>({});
  const [submittingResult, setSubmittingResult] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState<DriverAssignment[]>([]);
  const [selectedDriver1, setSelectedDriver1] = useState<string>('');
  const [selectedDriver2, setSelectedDriver2] = useState<string>('');
  const [selectedDriversForElimination, setSelectedDriversForElimination] = useState<string[]>([]);
  const [expandedRounds, setExpandedRounds] = useState<Set<number>>(new Set([1]));
  const [bracketStats, setBracketStats] = useState<BracketStatistics | null>(null);
  const [isAddingMatch, setIsAddingMatch] = useState(false);
  const [isRemovingMatch, setIsRemovingMatch] = useState(false);
  const [isGeneratingBracket, setIsGeneratingBracket] = useState(false);
  const [isCompletingRound, setIsCompletingRound] = useState(false);
  // Add state for search queries
  const [driver1Search, setDriver1Search] = useState('');
  const [driver2Search, setDriver2Search] = useState('');
  // Add state for dropdown open/close
  const [driver1DropdownOpen, setDriver1DropdownOpen] = useState(false);
  const [driver2DropdownOpen, setDriver2DropdownOpen] = useState(false);

  // Add refs for dropdowns
  const driver1DropdownRef = useRef<HTMLDivElement>(null);
  const driver2DropdownRef = useRef<HTMLDivElement>(null);

  // Click-away handler for dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        driver1DropdownRef.current &&
        !driver1DropdownRef.current.contains(event.target as Node)
      ) {
        setDriver1DropdownOpen(false);
      }
      if (
        driver2DropdownRef.current &&
        !driver2DropdownRef.current.contains(event.target as Node)
      ) {
        setDriver2DropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [eventId, categoryId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Debug logging for event/category IDs
      console.log('=== DEBUG: fetchData ===');
      console.log('eventId:', eventId);
      console.log('eventId type:', typeof eventId);
      console.log('categoryId:', categoryId);
      console.log('categoryId type:', typeof categoryId);
      console.log('categoryName:', categoryName);
      
      // First, let's get all registrations without joins to see what we have
      console.log('Executing query with:');
      console.log('- event_id =', eventId);
      console.log('- category_id =', categoryId);
      
      const { data: regData, error: regError } = await supabase
        .from('event_registrations')
        .select(`
          *,
          user:users(
            full_name,
            email,
            phone
          ),
          temporary_user:temporary_user!temporary_user_id(
            name,
            race_number
          ),
          driver_profile:driver_profiles(
            vehicle_type,
            vehicle_info,
            personal_info
          )
        `)
        .eq('event_id', eventId)
        .eq('category_id', categoryId)
        .not('status', 'eq', 'cancelled');

      if (regError) {
        console.error('Query error:', regError);
        throw regError;
      }
      
      console.log('Query executed successfully');
      
      // Debug logging for fetched registrations
      console.log('=== DEBUG: fetchData registrations ===');
      console.log('Raw registration data:', regData);
      console.log('Registration count:', regData?.length || 0);
      
      // Process registrations - use full_name from the registration table (populated by trigger)
      const flatRegistrations = (regData || []).map((reg: any) => ({
        ...reg,
        // The full_name field is populated by the populate_event_registration_full_name trigger
        // This should contain the driver's name regardless of whether they're a user or temporary_user
      }));
      
      console.log('Flattened registrations:', flatRegistrations.map(reg => ({
        id: reg.id,
        status: reg.status,
        full_name: reg.full_name,
        user_id: reg.user_id,
        temporary_user_id: reg.temporary_user_id,
        event_id: reg.event_id,
        category_id: reg.category_id,
        amount_paid: reg.amount_paid,
        payment_status: reg.payment_status
      })));
      console.log('=== END DEBUG ===');
      
      setRegistrations(flatRegistrations);

      // Fetch existing bracket
      const { data: bracketData, error: bracketError } = await supabase
        .from('tournament_brackets')
        .select('*')
        .eq('event_id', eventId)
        .eq('category_id', categoryId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (bracketError && bracketError.code !== 'PGRST116') throw bracketError;

      if (bracketData) {
        setBracket(bracketData);
        await loadBracketDetails(bracketData.id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load tournament data');
    } finally {
      setLoading(false);
    }
  };

  const loadBracketDetails = async (bracketId: string) => {
    try {
      const { data: matchesData, error: matchesError } = await supabase
        .from('tournament_matches')
        .select('*')
        .eq('bracket_id', bracketId)
        .order('round_number', { ascending: true })
        .order('match_number', { ascending: true });

      if (matchesError) throw matchesError;
      setMatches(matchesData || []);
    } catch (error) {
      console.error('Error loading bracket details:', error);
      toast.error('Failed to load bracket details');
    }
  };

  const handleCreateEliminationBracket = async () => {
    // Filter for only completed registrations (Ready to Compete)
    const completedRegistrations = registrations.filter(reg => reg.status === 'completed');
    
    if (completedRegistrations.length < 2) {
      toast.error('Need at least 2 drivers marked as "Ready to Compete" to create a bracket');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('generate_blank_elimination_bracket', {
        p_event_id: eventId,
        p_category_id: categoryId,
        p_bracket_name: `${categoryName} Elimination`,
        p_selected_drivers: completedRegistrations.length
      });
      if (error) throw error;
      await fetchData();
      toast.success('Elimination bracket created successfully!');
      if (onBracketCreated) onBracketCreated(data);
    } catch (error) {
      console.error('Error creating elimination bracket:', error);
      toast.error('Failed to create elimination bracket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fix: handleAssignDrivers should accept matchId
  const handleAssignDrivers = async (matchId: string) => {
    setSelectedMatch(matches.find(m => m.id === matchId) || null);
    
    // Debug logging
    console.log('=== DEBUG: handleAssignDrivers ===');
    console.log('Total registrations:', registrations.length);
    console.log('All registrations:', registrations.map(reg => ({
      id: reg.id,
      status: reg.status,
      driverName: reg.user?.full_name || reg.temporary_user?.name || 'Unknown Driver'
    })));
    
    // Show ALL registered drivers (all non-cancelled registrations)
    console.log('All registered drivers:', registrations.length);
    console.log('All registered drivers details:', registrations.map(reg => ({
      id: reg.id,
      status: reg.status,
      full_name: reg.full_name,
      user_id: reg.user_id,
      temporary_user_id: reg.temporary_user_id
    })));
    
    // Show ALL registered drivers (all non-cancelled registrations)
    setAvailableDrivers(
      registrations.map(reg => {
        const raceNumber = reg.driver_profile?.vehicle_info?.raceNumber || reg.temporary_user?.race_number || null;
        return {
          registrationId: reg.id,
          driverName: reg.full_name || 'Unknown Driver',
          raceNumber: raceNumber
        };
      })
    );
    
    console.log('Available drivers set:', registrations.map(reg => ({
      registrationId: reg.id,
      driverName: reg.full_name || 'Unknown Driver'
    })));
    console.log('=== END DEBUG ===');
    
    setDriver1Search('');
    setDriver2Search('');
    setShowDriverAssignmentModal(true);
  };

  const handleConfirmDriverAssignment = async () => {
    if (!selectedMatch || !selectedDriver1) {
      toast.error('Please select at least Driver 1');
      return;
    }

    try {
      const { error } = await supabase.rpc('assign_drivers_to_match', {
        p_match_id: selectedMatch.id,
        p_driver1_registration_id: selectedDriver1,
        p_driver2_registration_id: selectedDriver2 || null
      });

      if (error) throw error;

      await loadBracketDetails(bracket!.id);
      setShowDriverAssignmentModal(false);
      setSelectedDriver1('');
      setSelectedDriver2('');
      toast.success('Drivers assigned successfully!');
    } catch (error) {
      console.error('Error assigning drivers:', error);
      toast.error('Failed to assign drivers');
    }
  };

  const handleRecordMatchResult = async () => {
    // Check if this is a single driver match
    const isSingleDriverMatch = selectedMatch && selectedMatch.driver1_registration_id && !selectedMatch.driver2_registration_id;
    
    if (!selectedMatch || !matchResult.winnerRegistrationId) {
      toast.error('Please select a winner');
      return;
    }

    // For single driver matches, only require winner
    if (isSingleDriverMatch) {
      if (!matchResult.winnerRegistrationId) {
        toast.error('Please select a winner');
        return;
      }
    } else {
      // For two driver matches, require both winner and loser
      if (!matchResult.loserRegistrationId) {
        toast.error('Please select both winner and loser');
        return;
      }
    }

    setSubmittingResult(true);
    try {
      const { error } = await supabase.rpc('record_match_result_with_fouls_fixed', {
        p_match_id: selectedMatch.id,
        p_winner_registration_id: matchResult.winnerRegistrationId,
        p_loser_registration_id: isSingleDriverMatch ? null : matchResult.loserRegistrationId,
        p_winner_dial_in: matchResult.winnerDialIn ?? null,
        p_loser_dial_in: matchResult.loserDialIn ?? null,
        p_winner_elapsed_time: matchResult.winnerElapsedTime ?? null,
        p_loser_elapsed_time: matchResult.loserElapsedTime ?? null,
        p_winner_reaction_time: matchResult.winnerReactionTime ?? null,
        p_loser_reaction_time: matchResult.loserReactionTime ?? null,
        p_winner_foul: matchResult.winnerFoul ?? false,
        p_loser_foul: matchResult.loserFoul ?? false,
        p_notes: matchResult.notes ?? null
      });

      if (error) throw error;

      await loadBracketDetails(bracket!.id);
      setShowMatchResultModal(false);
      setMatchResult({});
      toast.success('Match result recorded successfully!');
    } catch (error) {
      console.error('Error recording match result:', error);
      toast.error('Failed to record match result');
    } finally {
      setSubmittingResult(false);
    }
  };

  const getDriverName = (registrationId?: string) => {
    if (!registrationId) return 'TBD';
    const registration = registrations.find(r => r.id === registrationId);
    const driverName = registration?.user?.full_name || registration?.temporary_user?.name || 'Unknown Driver';
    const raceNumber = registration?.driver_profile?.vehicle_info?.raceNumber || registration?.temporary_user?.race_number;
    return raceNumber ? `${driverName} (#${raceNumber})` : driverName;
  };

  const getMatchStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-500';
      case 'active': return 'text-yellow-500';
      case 'pending': return 'text-gray-500';
      default: return 'text-gray-400';
    }
  };

  const getMatchStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'active': return <Play className="h-4 w-4" />;
      case 'pending': return <Clock className="h-4 w-4" />;
      default: return <XCircle className="h-4 w-4" />;
    }
  };

  const formatTime = (time?: number) => {
    if (!time) return 'N/A';
    return time.toFixed(3);
  };

  const toggleRoundExpansion = (roundNumber: number) => {
    const newExpanded = new Set(expandedRounds);
    if (newExpanded.has(roundNumber)) {
      newExpanded.delete(roundNumber);
    } else {
      newExpanded.add(roundNumber);
    }
    setExpandedRounds(newExpanded);
  };

  const getMatchesByRound = () => {
    const matchesByRound: { [key: number]: TournamentMatch[] } = {};
    matches.forEach(match => {
      if (!matchesByRound[match.round_number]) {
        matchesByRound[match.round_number] = [];
      }
      matchesByRound[match.round_number].push(match);
    });
    return matchesByRound;
  };

  const getBracketTypeDisplay = () => {
    if (!bracket) return '';
    return bracket.bracket_type === 'qualifying' ? 'Qualifying' : 'Elimination';
  };

  const fetchBracketStatistics = useCallback(async () => {
    if (!bracket) return;
    
    try {
      const { data, error } = await supabase
        .rpc('get_bracket_statistics', { p_bracket_id: bracket.id });
      
      if (error) throw error;
      setBracketStats(data[0]);
    } catch (error) {
      console.error('Error fetching bracket statistics:', error);
    }
  }, [bracket]);

  const addMatch = async () => {
    if (!bracket) return;
    
    setIsAddingMatch(true);
    try {
      const { data, error } = await supabase
        .rpc('add_match_to_bracket', { 
          p_bracket_id: bracket.id,
          p_round_number: bracket.current_round
        });
      
      if (error) throw error;
      
      // Refresh matches
      await loadBracketDetails(bracket.id);
      await fetchBracketStatistics();
      
      toast.success(`Match added to Round ${bracket.current_round} successfully`);
    } catch (error) {
      console.error('Error adding match:', error);
      toast.error('Failed to add match');
    } finally {
      setIsAddingMatch(false);
    }
  };

  const deleteMatch = async (matchId: string) => {
    if (!bracket) return;
    
    setIsRemovingMatch(true);
    try {
      const { data, error } = await supabase
        .rpc('delete_match_flexible', {
          p_match_id: matchId
        });
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const result = data[0];
        if (result.success) {
          toast.success(result.message);
          // Refresh matches
          await loadBracketDetails(bracket.id);
          await fetchBracketStatistics();
        } else {
          toast.error(result.message);
        }
      } else {
        toast.error('Failed to delete match');
      }
    } catch (error) {
      console.error('Error deleting match:', error);
      toast.error('Failed to delete match');
    } finally {
      setIsRemovingMatch(false);
    }
  };

  const checkCanDeleteMatch = async (matchId: string) => {
    try {
      const { data, error } = await supabase
        .rpc('can_delete_match', {
          p_match_id: matchId
        });
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        return data[0];
      }
      
      return { can_delete: false, reason: 'Unknown error' };
    } catch (error) {
      console.error('Error checking match deletion status:', error);
      return { can_delete: false, reason: 'Error checking status' };
    }
  };

  const editMatch = async (matchId: string) => {
    const match = matches.find(m => m.id === matchId);
    if (!match) {
      toast.error('Match not found');
      return;
    }

    // Set up the match for editing
    setSelectedMatch(match);
    
    // Pre-fill the match result form
    setMatchResult({
      matchId: match.id,
      winnerRegistrationId: match.winner_registration_id || '',
      loserRegistrationId: match.loser_registration_id || '',
      winnerDialIn: match.driver1_registration_id === match.winner_registration_id ? match.driver1_dial_in : match.driver2_dial_in,
      loserDialIn: match.driver1_registration_id === match.loser_registration_id ? match.driver1_dial_in : match.driver2_dial_in,
      winnerElapsedTime: match.driver1_registration_id === match.winner_registration_id ? match.driver1_elapsed_time : match.driver2_elapsed_time,
      loserElapsedTime: match.driver1_registration_id === match.loser_registration_id ? match.driver1_elapsed_time : match.driver2_elapsed_time,
      winnerReactionTime: match.driver1_registration_id === match.winner_registration_id ? match.driver1_reaction_time : match.driver2_reaction_time,
      loserReactionTime: match.driver1_registration_id === match.loser_registration_id ? match.driver1_reaction_time : match.driver2_reaction_time,
      winnerFoul: match.driver1_registration_id === match.winner_registration_id ? match.driver1_foul : match.driver2_foul,
      loserFoul: match.driver1_registration_id === match.loser_registration_id ? match.driver1_foul : match.driver2_foul,
      notes: match.notes || ''
    });

    setShowMatchResultModal(true);
  };

  const resetMatch = async (matchId: string) => {
    try {
      const match = matches.find(m => m.id === matchId);
      if (!match) {
        toast.error('Match not found');
        return;
      }

      // Show warning toast with action button
      toast.error(
        `⚠️ This will reset the match to pending status and clear all results.`,
        {
          duration: 5000,
          action: {
            label: 'Reset Anyway',
            onClick: async () => {
              toast.promise(
                (async () => {
                  const { error } = await supabase
                    .from('tournament_matches')
                    .update({
                      status: 'pending',
                      winner_registration_id: null,
                      loser_registration_id: null,
                      driver1_dial_in: null,
                      driver2_dial_in: null,
                      driver1_elapsed_time: null,
                      driver2_elapsed_time: null,
                      driver1_reaction_time: null,
                      driver2_reaction_time: null,
                      driver1_foul: false,
                      driver2_foul: false,
                      notes: null
                    })
                    .eq('id', matchId);

                  if (error) throw error;

                  await loadBracketDetails(bracket!.id);
                  return 'Match reset successfully';
                })(),
                {
                  loading: 'Resetting match...',
                  success: (message) => message,
                  error: 'Failed to reset match'
                }
              );
            }
          }
        }
      );
    } catch (error) {
      console.error('Error resetting match:', error);
      toast.error('Failed to reset match');
    }
  };

  const generateQualifyingBracket = async () => {
    setIsGeneratingBracket(true);
    try {
      // First, get the count of valid registrations (not cancelled)
      const { data: registrations, error: regError } = await supabase
        .from('event_registrations')
        .select('id')
        .eq('event_id', eventId)
        .eq('category_id', categoryId)
        .not('status', 'eq', 'cancelled');
      
      if (regError) throw regError;
      
      const driverCount = registrations?.length || 0;
      
      if (driverCount === 0) {
        toast.error('No completed registrations found for this category');
        return;
      }
      
      // Calculate optimal match count
      const { data: optimalCount, error: countError } = await supabase
        .rpc('calculate_optimal_match_count', { p_driver_count: driverCount });
      
      if (countError) throw countError;
      
      // Generate bracket with dynamic match count
      const { data: bracketId, error } = await supabase
        .rpc('generate_blank_qualifying_bracket', {
          p_event_id: eventId,
          p_category_id: categoryId,
          p_bracket_name: 'Qualifying Bracket'
        });
      
      if (error) throw error;
      
      toast.success(`Generated qualifying bracket with ${optimalCount} matches for ${driverCount} drivers`);
      
      // Refresh brackets and matches
      await fetchData();
      await loadBracketDetails(bracketId);
      await fetchBracketStatistics();
      
    } catch (error) {
      console.error('Error generating qualifying bracket:', error);
      toast.error('Failed to generate qualifying bracket');
    } finally {
      setIsGeneratingBracket(false);
    }
  };

  const completeRound = async (roundNumber: number) => {
    if (!bracket) return;

    setIsCompletingRound(true);
    try {
      // Check if all matches in the round are completed
      const roundMatches = matches.filter(m => m.round_number === roundNumber);
      const completedMatches = roundMatches.filter(m => m.status === 'completed');
      
      if (completedMatches.length !== roundMatches.length) {
        toast.error(`Cannot complete round ${roundNumber}. All matches must be completed first.`);
        return;
      }

      // Check if this is the final round (only 1 match)
      if (roundMatches.length === 1) {
        toast.info(`This is the final match! Completing this round will end the tournament.`);
      }

      // Call the database function to advance the tournament with dynamic round calculation
      const { data, error } = await supabase.rpc('advance_tournament_round_dynamic', {
        p_bracket_id: bracket.id
      });

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      if (data) {
        // Check if tournament was completed
        if (roundMatches.length === 1) {
          toast.success(`Tournament completed! This was the final match.`);
          
          // Automatically recalculate tournament results when tournament is completed
          try {
            toast.info('Calculating final tournament results...');
            const { data: recalcData, error: recalcError } = await supabase.rpc('recalculate_tournament_results', {
              p_bracket_id: bracket.id
            });

            if (recalcError) {
              console.error('Error auto-recalculating tournament results:', recalcError);
              toast.error('Tournament completed but failed to calculate final results. Please click Recalculate manually.');
            } else if (recalcData) {
              toast.success('Tournament completed and final results calculated successfully!');
            } else {
              toast.warning('Tournament completed but no results were calculated. Please click Recalculate manually.');
            }
          } catch (recalcError) {
            console.error('Error auto-recalculating tournament results:', recalcError);
            toast.error('Tournament completed but failed to calculate final results. Please click Recalculate manually.');
          }
        } else {
          const nextRoundMatches = Math.ceil(roundMatches.length / 2);
          if (nextRoundMatches === 1) {
            toast.success(`Round ${roundNumber} completed! Created final championship match for round ${roundNumber + 1}.`);
          } else {
            toast.success(`Round ${roundNumber} completed! Created ${nextRoundMatches} matches for round ${roundNumber + 1}.`);
          }
        }
        
        await fetchData(); // Refresh data to show updated current round and results
      } else {
        // If the function returns false, it means not all matches are completed
        toast.error(`Cannot complete round ${roundNumber}. Please ensure all matches in this round are completed first.`);
      }
    } catch (error) {
      console.error('Error completing round:', error);
      toast.error('Failed to complete round');
    } finally {
      setIsCompletingRound(false);
    }
  };

  const goBackToRound = async (roundNumber: number) => {
    if (!bracket) return;

    // Check if the target round is complete
    const roundMatches = matches.filter(m => m.round_number === roundNumber);
    const completedMatches = roundMatches.filter(m => m.status === 'completed');
    const isRoundComplete = roundMatches.length > 0 && completedMatches.length === roundMatches.length;
    
    if (isRoundComplete) {
      // For complete rounds, just proceed with a simple confirmation
      toast.promise(
        (async () => {
          const { data, error } = await supabase.rpc('go_back_to_round', {
            p_bracket_id: bracket.id,
            p_target_round: roundNumber
          });

          if (error) throw error;
          if (!data) throw new Error('Failed to go back to round');



          await fetchData();
          return 'Successfully went back to round. You can now re-complete it or add matches.';
        })(),
        {
          loading: 'Going back to round...',
          success: (message) => message,
          error: 'Failed to go back to round'
        }
      );
    } else {
      // For incomplete rounds, show a more detailed warning
      toast.error(
        `⚠️ Round ${roundNumber} is incomplete. Going back will remove matches in later rounds that depend on this round.`,
        {
          duration: 5000,
          action: {
            label: 'Proceed Anyway',
            onClick: async () => {
              toast.promise(
                (async () => {
                  const { data, error } = await supabase.rpc('go_back_to_round', {
                    p_bracket_id: bracket.id,
                    p_target_round: roundNumber
                  });

                  if (error) throw error;
                  if (!data) throw new Error('Failed to go back to round');



                  await fetchData();
                  return 'Successfully went back to round. You can now add missing matches.';
                })(),
                {
                  loading: 'Going back to round...',
                  success: (message) => message,
                  error: 'Failed to go back to round'
                }
              );
            }
          }
        }
      );
        }
  };

  const canGoBackToRound = (roundNumber: number) => {
    if (!bracket) return false;
    
    // Can only go back to rounds that are less than or equal to the current round
    // and greater than 0
    return roundNumber > 0 && roundNumber <= bracket.current_round;
  };

  const getRoundStatus = (roundNumber: number) => {
    const roundMatches = matches.filter(m => m.round_number === roundNumber);
    const completedMatches = roundMatches.filter(m => m.status === 'completed');
    const totalMatches = roundMatches.length;
    
    if (totalMatches === 0) return 'empty';
    if (completedMatches.length === totalMatches) return 'completed';
    if (completedMatches.length > 0) return 'in-progress';
    return 'pending';
  };

  const recalculateTournamentResults = async () => {
    if (!bracket) return;

    try {
      const { data, error } = await supabase.rpc('recalculate_tournament_results', {
        p_bracket_id: bracket.id
      });

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      if (data) {
        toast.success('Tournament results recalculated successfully!');
        await fetchData(); // Refresh data to show updated results
      } else {
        toast.error('Failed to recalculate tournament results');
      }
    } catch (error) {
      console.error('Error recalculating tournament results:', error);
      toast.error('Failed to recalculate tournament results');
    }
  };



  useEffect(() => {
    if (bracket) {
      fetchBracketStatistics();
    }
  }, [bracket, fetchBracketStatistics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="h-8 w-8 animate-spin text-primary-500" />
          <span className="text-white text-lg font-medium">Loading tournament data...</span>
          <span className="text-gray-400 text-sm">Please wait while we fetch the latest information</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tournament Status */}
      <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-500/10 rounded-lg">
              <Trophy className="h-6 w-6 text-primary-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Tournament Management</h3>
              <p className="text-gray-400 text-sm">Manage qualifying and elimination brackets</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleCreateEliminationBracket}
              disabled={loading}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Bracket
            </button>
            {bracket && (
              <button
                onClick={recalculateTournamentResults}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all duration-200 shadow-lg"
                title="Recalculate tournament results to fix null positions and points"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Recalculate Results
              </button>
            )}
          </div>
        </div>

        {bracket && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
              <div className="flex items-center space-x-2 mb-2">
                <Target className="h-4 w-4 text-primary-500" />
                <span className="text-gray-400 text-sm font-medium">Bracket Type</span>
              </div>
              <span className="text-white font-semibold capitalize">{getBracketTypeDisplay()}</span>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="h-4 w-4 text-yellow-500" />
                <span className="text-gray-400 text-sm font-medium">Status</span>
              </div>
              <span className="text-white font-semibold capitalize">{bracket.status}</span>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
              <div className="flex items-center space-x-2 mb-2">
                <Users className="h-4 w-4 text-blue-500" />
                <span className="text-gray-400 text-sm font-medium">Total Drivers</span>
              </div>
              <span className="text-white font-semibold">{registrations.length}</span>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
              <div className="flex items-center space-x-2 mb-2">
                <Zap className="h-4 w-4 text-green-500" />
                <span className="text-gray-400 text-sm font-medium">Total Matches</span>
              </div>
              <span className="text-white font-semibold">{matches.length}</span>
            </div>
          </div>
        )}
      </div>

      {/* Add bracket statistics display */}
      {bracketStats && (
        <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-xl p-6 border border-zinc-700">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Zap className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Bracket Statistics</h3>
              <p className="text-gray-400 text-sm">Real-time match and driver statistics</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700 text-center">
              <div className="text-2xl font-bold text-blue-500">{bracketStats.total_matches}</div>
              <div className="text-gray-400 text-sm font-medium">Total Matches</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700 text-center">
              <div className="text-2xl font-bold text-green-500">{bracketStats.completed_matches}</div>
              <div className="text-gray-400 text-sm font-medium">Completed</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700 text-center">
              <div className="text-2xl font-bold text-yellow-500">{bracketStats.pending_matches}</div>
              <div className="text-gray-400 text-sm font-medium">Pending</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700 text-center">
              <div className="text-2xl font-bold text-orange-500">{bracketStats.active_matches}</div>
              <div className="text-gray-400 text-sm font-medium">Active</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700 text-center">
              <div className="text-2xl font-bold text-purple-500">{bracketStats.assigned_matches}</div>
              <div className="text-gray-400 text-sm font-medium">Assigned</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700 text-center">
              <div className="text-2xl font-bold text-red-500">{bracketStats.unassigned_matches}</div>
              <div className="text-gray-400 text-sm font-medium">Unassigned</div>
            </div>
          </div>
        </div>
      )}

      {/* Add match management controls */}
      {bracket && (
        <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-xl p-6 border border-zinc-700">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <Target className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Match Management</h3>
              <p className="text-gray-400 text-sm">Add, remove, and manage tournament matches</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={addMatch}
              disabled={isAddingMatch}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
            >
              {isAddingMatch ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2">Adding...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Match to Round {bracket?.current_round || 1}
                </>
              )}
            </button>
            
            <button
              onClick={() => {
                if (bracket) {
                  fetchBracketStatistics();
                }
              }}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Stats
            </button>
          </div>
        </div>
      )}

      {/* Enhanced Bracket Visualization */}
      {bracket && matches.length > 0 && (
        <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-xl p-6 border border-zinc-700">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Trophy className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Bracket Visualization</h3>
              <p className="text-gray-400 text-sm">View and manage tournament matches by round</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {Object.entries(getMatchesByRound()).map(([roundNumber, roundMatches]) => (
              <div key={roundNumber} className="border border-zinc-800 rounded-lg">
                <div className="flex items-center justify-between p-4 bg-zinc-800 hover:bg-zinc-700 transition-colors">
                  <button
                    onClick={() => toggleRoundExpansion(parseInt(roundNumber))}
                    className="flex items-center flex-1"
                  >
                    <div className="flex items-center">
                      <span className="text-white font-medium">
                        Round {roundNumber} ({roundMatches.length} matches)
                        {bracket && parseInt(roundNumber) === bracket.current_round && (
                          <span className="ml-2 px-2 py-1 bg-blue-600 text-white text-xs rounded-full">
                            Current
                          </span>
                        )}
                        {getRoundStatus(parseInt(roundNumber)) === 'completed' && (
                          <span className="ml-2 px-2 py-1 bg-green-600 text-white text-xs rounded-full">
                            Completed
                          </span>
                        )}
                      </span>
                      <span className="ml-2 text-sm text-gray-400">
                        {roundMatches.filter(m => m.status === 'completed').length} completed
                      </span>
                    </div>
                    {expandedRounds.has(parseInt(roundNumber)) ? (
                      <ChevronDown className="h-5 w-5 text-gray-400 ml-2" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400 ml-2" />
                    )}
                  </button>
                  
                  <div className="flex items-center space-x-2">
                    {/* Go Back to Round Button */}
                    {canGoBackToRound(parseInt(roundNumber)) && 
                     bracket && 
                     parseInt(roundNumber) < bracket.current_round && (
                      <button
                        onClick={() => goBackToRound(parseInt(roundNumber))}
                        className="flex items-center px-3 py-1 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all duration-200 text-sm font-medium"
                        title={`Go back to round ${roundNumber} to add matches or make corrections`}
                      >
                        <ArrowLeft className="h-3 w-3 mr-1" />
                        Go Back
                      </button>
                    )}
                    
                    {/* Complete Round Button */}
                    {roundMatches.filter(m => m.status === 'completed').length === roundMatches.length && 
                     roundMatches.length > 0 && 
                     bracket && 
                     parseInt(roundNumber) === bracket.current_round && (
                      <button
                        onClick={() => completeRound(parseInt(roundNumber))}
                        disabled={isCompletingRound}
                        className="flex items-center px-3 py-1 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium"
                        title={roundMatches.length === 1 ? "Complete final match and end tournament" : `Complete round and create ${Math.ceil(roundMatches.length / 2)} match${Math.ceil(roundMatches.length / 2) === 1 ? '' : 'es'} for next round`}
                      >
                        {isCompletingRound ? (
                          <>
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                            Completing...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {roundMatches.length === 1 ? 'Complete Final' : 'Complete Round'}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                
                {expandedRounds.has(parseInt(roundNumber)) && (
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {roundMatches.map((match) => (
                        <div key={match.id} className="bg-zinc-800/80 rounded-lg p-4 border border-zinc-700 hover:border-zinc-600 transition-all duration-200">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                              <span className="text-sm font-medium text-gray-300">
                                Match {match.match_number}
                              </span>
                            </div>
                            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getMatchStatusColor(match.status)}`}>
                              {getMatchStatusIcon(match.status)}
                              <span className="capitalize">{match.status}</span>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div className="flex justify-between items-center p-2 bg-zinc-700/50 rounded">
                              <span className="text-white text-sm font-medium">Driver 1:</span>
                              <span className="text-gray-300 text-sm font-semibold">
                                {getDriverName(match.driver1_registration_id)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center p-2 bg-zinc-700/50 rounded">
                              <span className="text-white text-sm font-medium">Driver 2:</span>
                              <span className="text-gray-300 text-sm font-semibold">
                                {getDriverName(match.driver2_registration_id)}
                              </span>
                            </div>

                            {match.status === 'completed' && (
                              <>
                                <div className="border-t border-zinc-700 pt-2 mt-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-green-500 text-sm font-medium">Winner:</span>
                                    <span className="text-green-500 text-sm font-medium">
                                      {getDriverName(match.winner_registration_id)}
                                    </span>
                                  </div>
                                  {match.driver1_dial_in && (
                                    <div className="text-xs text-gray-400 mt-1">
                                      Dial-ins: {formatTime(match.driver1_dial_in)} / {formatTime(match.driver2_dial_in)}
                                    </div>
                                  )}
                                  {match.driver1_foul && (
                                    <div className="text-xs text-red-400 mt-1">
                                      ⚠️ Fouls detected
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>

                          <div className="flex space-x-2 mt-4">
                            {match.status === 'pending' && (
                              <button
                                onClick={() => handleAssignDrivers(match.id)}
                                className="flex-1 flex items-center justify-center px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm hover:from-blue-700 hover:to-blue-800 transition-all duration-200"
                              >
                                <UserPlus className="h-3 w-3 mr-1" />
                                Assign
                              </button>
                            )}
                            {match.status === 'active' && (
                              <button
                                onClick={() => {
                                  setSelectedMatch(match);
                                  setShowMatchResultModal(true);
                                }}
                                className="flex-1 flex items-center justify-center px-3 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg text-sm hover:from-green-700 hover:to-green-800 transition-all duration-200"
                              >
                                <Edit3 className="h-3 w-3 mr-1" />
                                Record
                              </button>
                            )}
                            {/* Show Record button for single driver matches */}
                            {match.status === 'pending' && match.driver1_registration_id && !match.driver2_registration_id && (
                              <button
                                onClick={() => {
                                  setSelectedMatch(match);
                                  setShowMatchResultModal(true);
                                }}
                                className="flex-1 flex items-center justify-center px-3 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg text-sm hover:from-green-700 hover:to-green-800 transition-all duration-200"
                              >
                                <Edit3 className="h-3 w-3 mr-1" />
                                Record
                              </button>
                            )}
                            {/* Edit button for completed matches */}
                            {match.status === 'completed' && (
                              <button
                                onClick={() => editMatch(match.id)}
                                className="p-2 text-blue-500 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all duration-200"
                                title="Edit match results"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                            )}
                            
                            {/* Reset button for completed matches */}
                            {match.status === 'completed' && (
                              <button
                                onClick={() => resetMatch(match.id)}
                                className="p-2 text-orange-500 hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-all duration-200"
                                title="Reset match to pending status"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )}
                            
                            {/* Delete button for matches that can be deleted */}
                            {match.status !== 'completed' && 
                             !match.winner_registration_id && 
                             !match.loser_registration_id && (
                              <button
                                onClick={() => deleteMatch(match.id)}
                                disabled={isRemovingMatch}
                                className="p-2 text-red-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all duration-200"
                                title="Delete match (will unassign drivers if any are assigned)"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Driver Assignment Modal */}
      {showDriverAssignmentModal && selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-zinc-900 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">Assign Drivers to Match</h3>
            <div className="space-y-4">
              {/* Driver 1 Dropdown */}
              <div className="relative" ref={driver1DropdownRef}>
                <label className="block text-sm font-medium text-gray-300 mb-2">Driver 1</label>
                <button
                  type="button"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white text-left flex justify-between items-center"
                  onClick={() => setDriver1DropdownOpen((open) => !open)}
                  tabIndex={0}
                >
                  {selectedDriver1
                    ? (() => {
                        const driver = availableDrivers.find(d => d.registrationId === selectedDriver1);
                        return driver ? `${driver.driverName}${driver.raceNumber ? ` (#${driver.raceNumber})` : ''}` : 'Select Driver 1';
                      })()
                    : 'Select Driver 1'}
                  <span className="ml-2">▼</span>
                </button>
                {driver1DropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-zinc-800 border border-zinc-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <input
                      type="text"
                      value={driver1Search}
                      onChange={e => setDriver1Search(e.target.value)}
                      placeholder="Search name or race number"
                      className="w-full bg-zinc-700 border-b border-zinc-600 p-2 text-white sticky top-0 z-20"
                      autoFocus
                    />
                    <div className="max-h-48 overflow-y-auto">
                      {(() => {
                        const filteredDrivers = availableDrivers.filter(driver => {
                          const query = driver1Search.toLowerCase();
                          return (
                            driver.driverName.toLowerCase().includes(query) ||
                            (driver.raceNumber && driver.raceNumber.toString().toLowerCase().includes(query))
                          );
                        });
                        
                        console.log('=== DEBUG: Driver 1 Search ===');
                        console.log('Search query:', driver1Search);
                        console.log('Total available drivers:', availableDrivers.length);
                        console.log('Filtered drivers:', filteredDrivers.length);
                        console.log('Available drivers:', availableDrivers.map(d => ({ name: d.driverName, raceNumber: d.raceNumber })));
                        console.log('Filtered drivers:', filteredDrivers.map(d => ({ name: d.driverName, raceNumber: d.raceNumber })));
                        console.log('=== END DEBUG ===');
                        
                        if (filteredDrivers.length === 0) {
                          return (
                            <div className="px-4 py-2 text-gray-400 text-center">
                              {availableDrivers.length === 0 ? 'No drivers available' : 'No drivers found'}
                            </div>
                          );
                        }
                        
                        return filteredDrivers.map(driver => (
                          <button
                            key={driver.registrationId}
                            type="button"
                            className={`w-full text-left px-4 py-2 hover:bg-primary-600 hover:text-white ${selectedDriver1 === driver.registrationId ? 'bg-primary-700 text-white' : 'text-gray-200'}`}
                            onClick={() => {
                              setSelectedDriver1(driver.registrationId);
                              setDriver1DropdownOpen(false);
                            }}
                          >
                            {driver.driverName} {driver.raceNumber ? `(#${driver.raceNumber})` : ''}
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
              {/* Driver 2 Dropdown */}
              <div className="relative" ref={driver2DropdownRef}>
                <label className="block text-sm font-medium text-gray-300 mb-2">Driver 2 (Optional)</label>
                <button
                  type="button"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white text-left flex justify-between items-center"
                  onClick={() => setDriver2DropdownOpen((open) => !open)}
                  tabIndex={0}
                >
                  {selectedDriver2
                    ? (() => {
                        const driver = availableDrivers.find(d => d.registrationId === selectedDriver2);
                        return driver ? `${driver.driverName}${driver.raceNumber ? ` (#${driver.raceNumber})` : ''}` : 'Select Driver 2';
                      })()
                    : 'Select Driver 2 (Optional)'}
                  <span className="ml-2">▼</span>
                </button>
                {driver2DropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-zinc-800 border border-zinc-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <input
                      type="text"
                      value={driver2Search}
                      onChange={e => setDriver2Search(e.target.value)}
                      placeholder="Search name or race number"
                      className="w-full bg-zinc-700 border-b border-zinc-600 p-2 text-white sticky top-0 z-20"
                      autoFocus
                    />
                    <div className="max-h-48 overflow-y-auto">
                      {(() => {
                        const filteredDrivers = availableDrivers.filter(driver => {
                          const query = driver2Search.toLowerCase();
                          return (
                            driver.driverName.toLowerCase().includes(query) ||
                            (driver.raceNumber && driver.raceNumber.toString().toLowerCase().includes(query))
                          );
                        });
                        
                        console.log('=== DEBUG: Driver 2 Search ===');
                        console.log('Search query:', driver2Search);
                        console.log('Total available drivers:', availableDrivers.length);
                        console.log('Filtered drivers:', filteredDrivers.length);
                        console.log('=== END DEBUG ===');
                        
                        if (filteredDrivers.length === 0) {
                          return (
                            <div className="px-4 py-2 text-gray-400 text-center">
                              {availableDrivers.length === 0 ? 'No drivers available' : 'No drivers found'}
                            </div>
                          );
                        }
                        
                        return filteredDrivers.map(driver => (
                          <button
                            key={driver.registrationId}
                            type="button"
                            className={`w-full text-left px-4 py-2 hover:bg-primary-600 hover:text-white ${selectedDriver2 === driver.registrationId ? 'bg-primary-700 text-white' : 'text-gray-200'}`}
                            onClick={() => {
                              setSelectedDriver2(driver.registrationId);
                              setDriver2DropdownOpen(false);
                            }}
                          >
                            {driver.driverName} {driver.raceNumber ? `(#${driver.raceNumber})` : ''}
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 p-3 bg-zinc-800 rounded-lg">
              <p className="text-sm text-gray-400">
                <strong>Note:</strong> You can assign just one driver for bye rounds or time trials. 
                Driver 2 is optional and can be added later.
              </p>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleConfirmDriverAssignment}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Assign Drivers
              </button>
              <button
                onClick={() => setShowDriverAssignmentModal(false)}
                className="flex-1 px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Match Result Modal */}
      {showMatchResultModal && selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-zinc-900 rounded-lg p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-white mb-4">Record Match Result</h3>
            
            {/* Check if this is a single driver match */}
            {selectedMatch.driver1_registration_id && !selectedMatch.driver2_registration_id && (
              <div className="mb-4 p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
                <p className="text-sm text-blue-300">
                  <strong>Single Driver Match:</strong> Only one driver is assigned to this match. 
                  You can record the winner's performance data.
                </p>
              </div>
            )}
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Winner</label>
                  <select
                    value={matchResult.winnerRegistrationId || ''}
                    onChange={(e) => setMatchResult({...matchResult, winnerRegistrationId: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  >
                    <option value="">Select Winner</option>
                    <option value={selectedMatch.driver1_registration_id}>
                      {getDriverName(selectedMatch.driver1_registration_id)}
                    </option>
                    {selectedMatch.driver2_registration_id && (
                      <option value={selectedMatch.driver2_registration_id}>
                        {getDriverName(selectedMatch.driver2_registration_id)}
                      </option>
                    )}
                  </select>
                </div>

                {selectedMatch.driver2_registration_id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Loser</label>
                    <select
                      value={matchResult.loserRegistrationId || ''}
                      onChange={(e) => setMatchResult({...matchResult, loserRegistrationId: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    >
                      <option value="">Select Loser</option>
                      <option value={selectedMatch.driver1_registration_id}>
                        {getDriverName(selectedMatch.driver1_registration_id)}
                      </option>
                      <option value={selectedMatch.driver2_registration_id}>
                        {getDriverName(selectedMatch.driver2_registration_id)}
                      </option>
                    </select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Winner Dial-in</label>
                  <input
                    type="number"
                    step="0.001"
                    value={matchResult.winnerDialIn || ''}
                    onChange={(e) => setMatchResult({...matchResult, winnerDialIn: parseFloat(e.target.value)})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    placeholder="0.000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Loser Dial-in</label>
                  <input
                    type="number"
                    step="0.001"
                    value={matchResult.loserDialIn || ''}
                    onChange={(e) => setMatchResult({...matchResult, loserDialIn: parseFloat(e.target.value)})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    placeholder="0.000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Winner ET</label>
                  <input
                    type="number"
                    step="0.001"
                    value={matchResult.winnerElapsedTime || ''}
                    onChange={(e) => setMatchResult({...matchResult, winnerElapsedTime: parseFloat(e.target.value)})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    placeholder="0.000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Loser ET</label>
                  <input
                    type="number"
                    step="0.001"
                    value={matchResult.loserElapsedTime || ''}
                    onChange={(e) => setMatchResult({...matchResult, loserElapsedTime: parseFloat(e.target.value)})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    placeholder="0.000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Winner RT</label>
                  <input
                    type="number"
                    step="0.001"
                    value={matchResult.winnerReactionTime || ''}
                    onChange={(e) => setMatchResult({...matchResult, winnerReactionTime: parseFloat(e.target.value)})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    placeholder="0.000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Loser RT</label>
                  <input
                    type="number"
                    step="0.001"
                    value={matchResult.loserReactionTime || ''}
                    onChange={(e) => setMatchResult({...matchResult, loserReactionTime: parseFloat(e.target.value)})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                    placeholder="0.000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={matchResult.winnerFoul || false}
                      onChange={(e) => setMatchResult({...matchResult, winnerFoul: e.target.checked})}
                      className="mr-2 rounded border-zinc-600 bg-zinc-700 text-primary-500"
                    />
                    Winner Foul (Breakout)
                  </label>
                </div>

                <div>
                  <label className="flex items-center text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={matchResult.loserFoul || false}
                      onChange={(e) => setMatchResult({...matchResult, loserFoul: e.target.checked})}
                      className="mr-2 rounded border-zinc-600 bg-zinc-700 text-primary-500"
                    />
                    Loser Foul (Breakout)
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                <textarea
                  value={matchResult.notes || ''}
                  onChange={(e) => setMatchResult({...matchResult, notes: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  rows={3}
                  placeholder="Additional notes..."
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleRecordMatchResult}
                disabled={submittingResult}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {submittingResult ? 'Recording...' : 'Record Result'}
              </button>
              <button
                onClick={() => setShowMatchResultModal(false)}
                className="flex-1 px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 